import { useMutation, useQuery } from '@tanstack/react-query';
import { Bot, Undo2, User } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button, Select } from '@/components/ui';
import { api } from '@/lib/api';
import { relTime } from '@/lib/utils';

const UNDOABLE = new Set([
  'node.create',
  'node.update',
  'node.done',
  'node.drop',
  'node.move',
  'node.reorder',
  'node.tag',
  'node.untag',
  'node.archive',
  'node.restore',
  'node.convert',
]);

function describe(diff: unknown): string {
  if (!diff || typeof diff !== 'object') return '';
  const d = diff as Record<string, unknown>;
  if ('after' in d && Object.keys(d).length === 1) return '';
  return Object.entries(d)
    .filter(([, v]) => v && typeof v === 'object' && 'after' in (v as object))
    .map(
      ([k, v]) =>
        `${k}: ${JSON.stringify((v as { before: unknown }).before)} → ${JSON.stringify((v as { after: unknown }).after)}`,
    )
    .join(', ');
}

export function ActivityTimeline({
  nodeId,
  limit = 50,
  showNode,
  defaultKind = '',
}: {
  nodeId?: number;
  limit?: number;
  showNode?: boolean;
  defaultKind?: '' | 'human' | 'agent';
}) {
  const [kind, setKind] = useState<'' | 'human' | 'agent'>(defaultKind);
  const undo = useMutation({
    mutationFn: (id: number) => api.undo(id),
    onSuccess: () => toast.success('Change undone'),
  });
  const { data } = useQuery({
    queryKey: ['activity', nodeId, kind, limit],
    queryFn: () => api.activity({ node: nodeId, kind: kind || undefined, limit }),
  });
  return (
    <div className="space-y-2">
      <Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} className="w-40">
        <option value="">Humans and agents</option>
        <option value="human">Humans only</option>
        <option value="agent">Agents only</option>
      </Select>
      <ol className="space-y-2">
        {data?.length === 0 && <li className="text-sm text-muted-fg">No activity.</li>}
        {data?.map((a) => (
          <li key={a.id} className="flex gap-2 text-sm">
            <span className="mt-0.5">
              {a.actor_kind === 'agent' ? (
                <Bot size={14} className="text-agent" />
              ) : (
                <User size={14} className="text-muted-fg" />
              )}
            </span>
            <div className="min-w-0">
              <div>
                <span className="font-medium">{a.actor ?? 'system'}</span>{' '}
                <span className="text-muted-fg">{a.action}</span>
                {showNode && a.node_id && (
                  <>
                    {' '}
                    <Link to={`?n=${a.node_id}`} className="text-primary hover:underline">
                      {a.node_title ?? `#${a.node_id}`}
                    </Link>
                  </>
                )}
                <span className="ml-1 text-xs text-muted-fg">{relTime(a.created_at)}</span>
                {a.node_id && UNDOABLE.has(a.action) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-1 h-5 px-1 text-xs"
                    title="Undo this change"
                    onClick={() => confirm('Undo this change?') && undo.mutate(a.id)}
                  >
                    <Undo2 size={11} /> undo
                  </Button>
                )}
              </div>
              {a.message && <div className="whitespace-pre-wrap text-muted-fg">{a.message}</div>}
              {describe(a.diff) && <div className="truncate text-xs text-muted-fg">{describe(a.diff)}</div>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
